<script setup lang="ts">
import { eventBus } from "#bb/lib/eventBus";
import { cn } from "@/lib/utils";
import { useTemplating } from "#bb/composables/useTemplating";
import { toast } from "vue-sonner";
import { ComponentDefinitionIds } from "#bb/lib/componentDefinitionIds";
import { resolveCandidateScopedFileApiPath } from "#bb/lib/pathContract";

/** 1) Expect `id` from the parent. */
const props = defineProps<{ id: string }>();

/** 2) Store read methods for definition data. */
const { getContentById, getResolvedDataPath } = useContentManager();
const definitionData = computed(() => getContentById(props.id) || {});

/** 3) Context-menu commands. */
const commands = [
  {
    id: "editors-group",
    type: "group",
    name: "Editors",
    description: "Voice note recorder editor",
    commands: [
      {
        id: "edit",
        name: "Edit",
        description: "Edit the voice note recorder settings",
        action: async () => {
          eventBus.emit(`component:onEdit:${props.id}`, {
            data: definitionData.value,
          });
        },
        icon: "lucide:edit",
      },
    ],
  },
];

/** 5) Expose properties for context menu. */
defineExpose({
  commands,
  properties: {
    id: props.id,
    name: "Voice Note",
    icon: "lucide:mic",
  },
});

const { t } = useI18n({ useScope: "local" });
const route = useRoute();
const { siteId } = useSite();

const { profile } = useProfile();
const userOverrideId = computed(() => {
  const value = route.query.user;
  if (Array.isArray(value)) return value[0] ?? "";
  return typeof value === "string" ? value : "";
});
const profileId = computed(() => userOverrideId.value || profile.value?.id || "");

const templateContext = computed(() => ({
  user: profile.value,
  userProfileId: profileId.value,
  siteId: siteId.value,
}));
const { applyTemplate } = useTemplating(templateContext, { silent: true });

function resolveTemplate(value: string | undefined) {
  if (!value) return "";
  if (!value.includes("{{")) return value;
  if (!profileId.value) return value;
  return applyTemplate(value);
}

const resolvedContainerPath = computed(() =>
  resolveTemplate(definitionData.value.containerPath)
);
const resolvedPath = computed(() =>
  resolveTemplate(definitionData.value.path ?? "voice-notes")
);

const storageResolution = computed(() => {
  try {
    return {
      path: resolveCandidateScopedFileApiPath(
        resolvedContainerPath.value,
        resolvedPath.value,
        profileId.value,
        "Voice Note storage path",
      ),
      error: null,
    };
  } catch (error) {
    return {
      path: "",
      error: error instanceof Error
        ? error.message
        : "Voice Note storage path could not be resolved",
    };
  }
});
const folderPath = computed(() => storageResolution.value.path);
const storageError = computed(() => storageResolution.value.error);

function isNotFoundError(err: any) {
  const status =
    err?.statusCode ??
    err?.status ??
    err?.response?.status ??
    err?.data?.statusCode ??
    err?.data?.status;
  return status === 404;
}

function createEmptyFolder(path: string) {
  const segments = path.split("/").filter(Boolean);
  const name = segments[segments.length - 1] || "";
  return {
    id: "",
    parentId: "",
    siteId: siteId.value ?? "",
    name,
    slug: "",
    path,
    type: "text/directory",
    length: null,
    children: [],
    versions: [],
    permissions: [],
    metadata: {},
    parents: [],
  };
}

const { getAnswer, setAnswer } = useAnswers();
const answerPayload = computed(() => ({
  type: ComponentDefinitionIds.VOICE_NOTE,
  value: folderPath.value,
  path: getResolvedDataPath(props.id).join("/"),
  binding: definitionData.value,
  data: {
    userProfileId: profileId.value,
    siteId: siteId.value,
    containerId: definitionData.value.containerId,
    containerPath: resolvedContainerPath.value,
    path: resolvedPath.value,
    storagePath: folderPath.value,
  },
}));

const ensureAnswer = async () => {
  if (!props.id || storageError.value) return;
  const payload = answerPayload.value;
  const existing = await getAnswer(props.id, payload.path);
  if (
    !existing ||
    !existing.data?.storagePath ||
    existing.data?.storagePath !== payload.data.storagePath ||
    existing.data?.userProfileId !== payload.data.userProfileId ||
    existing.data?.containerPath !== payload.data.containerPath ||
    existing.data?.path !== payload.data.path
  ) {
    await setAnswer(props.id, payload);
  }
};

const { get, create, uploading } = useFileEntries(siteId);
const siteFilesState = reactive<{ files: any; folderPath: string }>({
  files: createEmptyFolder(folderPath.value),
  folderPath: folderPath.value,
});

async function loadFiles(path: string) {
  siteFilesState.folderPath = path;
  try {
    const entries = await get(path, "flags,metadata");
    siteFilesState.files = entries || createEmptyFolder(path);
  } catch (err) {
    if (isNotFoundError(err)) {
      siteFilesState.files = createEmptyFolder(path);
      return;
    }
    throw err;
  }
}

if (folderPath.value) {
  await loadFiles(folderPath.value);
}

const maxDurationSeconds = computed<number>(() => {
  const raw = Number(definitionData.value.maxDurationSeconds);
  return Number.isFinite(raw) && raw > 0 ? raw : 300;
});
const filePrefix = computed<string>(
  () => definitionData.value.filePrefix || "voice-note"
);

const recorder = useVoiceRecorder({
  maxDurationSeconds,
  onMaxDurationReached: () => {
    toast.info(t("maxDurationReached"));
  },
});

const elapsedSeconds = computed(() =>
  Math.floor(recorder.elapsedMs.value / 1000)
);

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

function timestampSlug() {
  return new Date().toISOString().replace(/[:.]/g, "-");
}

async function startRecording() {
  try {
    await recorder.start();
  } catch (err: any) {
    const message =
      err?.name === "NotAllowedError"
        ? t("micPermissionDenied")
        : err?.message || t("recordFailed");
    toast.error(message);
  }
}

async function stopAndUpload() {
  try {
    const file = await recorder.stop();
    const filename = `${filePrefix.value}-${timestampSlug()}.${recorder.extension()}`;
    const renamed = new File([file], filename, { type: file.type });
    await create(folderPath.value, renamed, {
      mkdir: definitionData.value.mkdirOnUpload ?? true,
      filename,
    });
    toast.success(t("uploadSuccess"));
    await loadFiles(folderPath.value);
  } catch (err: any) {
    toast.error(err?.message || t("uploadFailed"));
  }
}

function cancelRecording() {
  recorder.cancel();
}

onMounted(() => {
  ensureAnswer();
  watch(
    [folderPath, resolvedContainerPath, resolvedPath, profileId, siteId],
    () => {
      ensureAnswer();
    }
  );
  watch(folderPath, (newPath) => {
    if (newPath) {
      loadFiles(newPath);
    }
  });
});

onBeforeUnmount(() => {
  recorder.cancel();
});

provide("siteFiles", siteFilesState);

const showExistingList = computed(
  () => definitionData.value.showExistingList !== false
);

const meterPercent = computed(() =>
  Math.min(100, Math.round(recorder.level.value * 140))
);
const progressPercent = computed(() => {
  const max = maxDurationSeconds.value;
  if (!max) return 0;
  return Math.min(100, (elapsedSeconds.value / max) * 100);
});
</script>

<template>
  <div :class="cn('space-y-4', $attrs.class ?? '')">
    <div
      v-if="storageError"
      class="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive"
      role="alert"
    >
      {{ storageError }}
    </div>
    <template v-else>
    <div class="rounded-lg border border-muted-foreground/30 p-4 space-y-3">
      <div class="flex items-center gap-3">
        <icon mode="svg" name="lucide:mic" class="text-2xl text-primary" />
        <div class="flex-1">
          <p class="text-sm font-semibold">{{ t("recordTitle") }}</p>
          <p class="text-xs text-muted-foreground">
            {{
              t("recordHint", {
                duration: formatTime(maxDurationSeconds),
              })
            }}
          </p>
        </div>
        <p class="text-sm tabular-nums text-muted-foreground">
          {{ formatTime(elapsedSeconds) }} /
          {{ formatTime(maxDurationSeconds) }}
        </p>
      </div>

      <div
        class="h-1 w-full overflow-hidden rounded-full bg-muted"
        :aria-hidden="!recorder.isRecording.value"
      >
        <div
          class="h-full bg-primary transition-[width] duration-100 ease-linear"
          :style="{ width: `${progressPercent}%` }"
        />
      </div>

      <div
        v-if="recorder.isRecording.value"
        class="h-2 w-full overflow-hidden rounded-full bg-muted"
        aria-hidden="true"
      >
        <div
          class="h-full bg-destructive transition-[width] duration-75"
          :style="{ width: `${meterPercent}%` }"
        />
      </div>

      <div class="flex items-center gap-2">
        <Button
          v-if="!recorder.isRecording.value"
          type="button"
          variant="default"
          :disabled="!recorder.isSupported || uploading"
          @click="startRecording"
        >
          <icon mode="svg" name="lucide:mic" class="mr-2 text-base" />
          {{ t("startRecording") }}
        </Button>

        <template v-else>
          <Button
            v-if="!recorder.isPaused.value"
            type="button"
            variant="secondary"
            @click="recorder.pause()"
          >
            <icon mode="svg" name="lucide:pause" class="mr-2 text-base" />
            {{ t("pause") }}
          </Button>
          <Button
            v-else
            type="button"
            variant="secondary"
            @click="recorder.resume()"
          >
            <icon mode="svg" name="lucide:play" class="mr-2 text-base" />
            {{ t("resume") }}
          </Button>
          <Button
            type="button"
            variant="default"
            :disabled="uploading"
            @click="stopAndUpload"
          >
            <icon mode="svg" name="lucide:square" class="mr-2 text-base" />
            {{ uploading ? t("uploading") : t("stopAndSave") }}
          </Button>
          <Button
            type="button"
            variant="ghost"
            :disabled="uploading"
            @click="cancelRecording"
          >
            {{ t("cancel") }}
          </Button>
        </template>
      </div>

      <p v-if="!recorder.isSupported" class="text-xs text-destructive">
        {{ t("notSupported") }}
      </p>
    </div>

    <site-files
      v-if="showExistingList"
      :path="folderPath"
      :enable-navigation="false"
      :show-back-button="false"
      :include-mime-types="['audio/*']"
      :action-filter="definitionData.actionFilter"
    />
    </template>
  </div>
</template>

<script lang="ts">
import { ComponentDefinitionIds } from "#bb/lib/componentDefinitionIds";
export default {
  componentId: ComponentDefinitionIds.VOICE_NOTE,
  editor: () => import("#bb/components/editor/media/voice-note.vue"),
  icon: "lucide:mic",
  type: "Voice Note",
  description:
    "A building block for recording voice notes from the browser and saving them to the user's data directory.",
  default: {
    containerId: crypto.randomUUID(),
    containerPath: "/",
    path: "voice-notes",
    maxDurationSeconds: 300,
    showExistingList: true,
    mkdirOnUpload: true,
    filePrefix: "voice-note",
  },
  categories: ["Media"],
  order: 13055,
};
</script>

<i18n lang="yaml">
en:
  recordTitle: "Record a voice note"
  recordHint: "Tap record, speak, then save. Max length: {duration}."
  startRecording: "Record"
  pause: "Pause"
  resume: "Resume"
  stopAndSave: "Stop & save"
  uploading: "Uploading…"
  cancel: "Cancel"
  notSupported: "Audio recording is not supported in this browser."
  micPermissionDenied: "Microphone access was denied. Please grant permission to record."
  recordFailed: "Failed to start recording."
  uploadFailed: "Failed to save voice note."
  uploadSuccess: "Voice note saved."
  maxDurationReached: "Reached maximum recording length."
</i18n>
